import { useCallback, useMemo } from 'react';
import { InferredProgressOptions } from 'api/project';
import { Field } from 'api/tasks';
import {
  FieldType,
  FilterableField,
  SourceType,
} from 'components/organisms/tasks/fields';
import { PermissionRuleName } from 'constant';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useTranslation } from 'hooks/use-translation';
import { ProjectFieldIds } from './constants';
import { useCurrentPhasesField } from './use-current-phases-field';
import { useCustomerField } from './use-customer-field';
import { usePartnerCompanyField } from './use-partner-company-field';
import { useProjectOwnerField } from './use-project-owner-field';
import { useTeamMembersField } from './use-team-members-field';
import { useTemplateField } from './use-template-field';

const FIELD_ID_ARR = -1;
const FIELD_ID_PROJECT_FEE = FIELD_ID_ARR - 1;
const FIELD_ID_PROJECT_OWNER = FIELD_ID_PROJECT_FEE - 1;
const FIELD_ID_TEMPLATE = FIELD_ID_PROJECT_OWNER - 1;
const FIELD_ID_INFERRED_PROGRESS = FIELD_ID_TEMPLATE - 1;
const FIELD_ID_DUE_DATE = FIELD_ID_INFERRED_PROGRESS - 1;
const FIELD_ID_START_DATE = FIELD_ID_DUE_DATE - 1;
const FIELD_ID_LAST_STATUS_UPDATE = FIELD_ID_START_DATE - 1;
const FIELD_ID_ESTIMATED_DUE_DATE = FIELD_ID_LAST_STATUS_UPDATE - 1;
const FIELD_ID_PROJECT_NAME = FIELD_ID_ESTIMATED_DUE_DATE - 1;
const FIELD_ID_CUSTOMER = FIELD_ID_PROJECT_NAME - 1;
const FIELD_ID_PROGRESS_PERCENT = FIELD_ID_CUSTOMER - 1;
const FIELD_ID_TEAM_MEMBERS = FIELD_ID_PROGRESS_PERCENT - 1;
const FIELD_ID_CURRENT_PHASES = FIELD_ID_TEAM_MEMBERS - 1;
const FIELD_ID_CREATED_ON = FIELD_ID_CURRENT_PHASES - 1;

export const FIELD_ID_RATINGS = FIELD_ID_CURRENT_PHASES - 1;
const FIELD_ID_PARNTER_COMPANIES = FIELD_ID_RATINGS - 1;
const FIELD_ID_PROJECT_AGE = ProjectFieldIds.ProjectAge;
const FIELD_ID_PLANNED_DURATION = ProjectFieldIds.PlannedDuration;
const FIELD_ID_ACTUAL_START_DATE = ProjectFieldIds.ActualStartDate;
const FIELD_ID_ACTUAL_COMPLETED_DATE = ProjectFieldIds.ActualCompletdDate;
const FIELD_ID_BUDGETED_HOURS = FIELD_ID_ACTUAL_COMPLETED_DATE - 1;
const FIELD_ID_TRACKED_HOURS = FIELD_ID_BUDGETED_HOURS - 1;
const FIELD_ID_ALLOCATED_HOURS = FIELD_ID_TRACKED_HOURS - 1;
const FIELD_ID_CUSTOMERS_INVITED = FIELD_ID_ALLOCATED_HOURS - 1;
const FIELD_ID_CUSTOMERS_ACCEPTED = FIELD_ID_CUSTOMERS_INVITED - 1;

export const ProjectTimeTrackingFieldsName = {
  trackedHours: 'totalMinutes',
  allocatedHours: 'totalAllocatedHours',
  budgetedHours: 'budgetedHours',
};

const NativeSensitiveFields = [
  'arr',
  'projectFee',
  'annualizedRecurringRevenue',
];
const HoursFields = [
  'projectHours',
  ProjectTimeTrackingFieldsName.budgetedHours,
  ProjectTimeTrackingFieldsName.allocatedHours,
  ProjectTimeTrackingFieldsName.trackedHours,
];

export const useProjectFieldsAccess = (): ((
  field: FilterableField | Field,
) => boolean) => {
  const { isAllowedForCurrentUser } = usePermissionForRule();
  const hasAccessToNativeSensitiveFields = isAllowedForCurrentUser(
    PermissionRuleName.ProjectNativeMoneyFieldSection,
  );
  const hasAccessToCustomFields = isAllowedForCurrentUser(
    PermissionRuleName.ProjectCustomFieldSection,
  );
  const hasAccessToProjectHoursFields = isAllowedForCurrentUser(
    PermissionRuleName.ProjectHoursSection,
  );

  return useCallback(
    ({ fieldName, customField }: FilterableField | Field) => {
      if (NativeSensitiveFields.includes(fieldName)) {
        return hasAccessToNativeSensitiveFields;
      }
      if (HoursFields.includes(fieldName)) {
        return hasAccessToProjectHoursFields;
      }
      if (customField) {
        return hasAccessToCustomFields;
      }

      return true;
    },
    [
      hasAccessToCustomFields,
      hasAccessToNativeSensitiveFields,
      hasAccessToProjectHoursFields,
    ],
  );
};

export const useProjectNativeFieldsAsFilterableFields = (): {
  loading: boolean;
  fields: FilterableField[];
} => {
  const arr_label = useTranslation('project.fields.arr');
  const project_name_label = useTranslation('project.fields.project_name');
  const project_fee_label = useTranslation('project.fields.project_fee');
  const due_date_label = useTranslation('project.fields.due_date');
  const start_date_label = useTranslation('project.fields.start_date');
  const last_status_update_label = useTranslation(
    'project.fields.last_status_update',
  );
  const [isTimeSheetEnabled] = useAccountFeature(AccountFeature.Timesheets);
  const progress_percent_label = useTranslation(
    'project.fields.progress_percent',
  );
  const inferred_progress_label = useTranslation(
    'project.fields.inferred_progress',
  );
  const project_age_label = useTranslation('project.fields.project_age');
  const planned_duration_label = useTranslation(
    'project.fields.planned_duration',
  );
  const actual_start_date_label = useTranslation(
    'project.fields.actual_start_date',
  );
  const actual_completed_date_label = useTranslation(
    'project.fields.actual_completed_date',
  );
  const trackedHoursLabel = 'Tracked hours';
  const budgetHoursLabel = 'Budgeted hours';
  const allocatedHoursLabel = 'Allocated hours';
  const customersInvitedLabel = useTranslation(
    'project.fields.customers_invited',
  );
  const customersAcceptedLabel = useTranslation(
    'project.fields.customers_accepted',
  );
  const projectCreatedOnLabel = useTranslation('project.fields.created_on');
  const projectFields = useMemo(
    () =>
      [
        {
          fieldName: 'arr',
          fieldLabel: arr_label,
          type: FieldType.Number,
          fieldId: FIELD_ID_ARR,
        },
        {
          fieldName: 'projectName',
          fieldLabel: project_name_label,
          type: FieldType.Text,
          fieldId: FIELD_ID_PROJECT_NAME,
        },
        {
          fieldName: 'projectFee',
          fieldLabel: project_fee_label,
          type: FieldType.Number,
          fieldId: FIELD_ID_PROJECT_FEE,
        },
        {
          fieldName: 'inferredProgress',
          fieldLabel: inferred_progress_label,
          type: FieldType.SingleSelect,
          fieldId: FIELD_ID_INFERRED_PROGRESS,
          optionsAvailable: true,
          metaData: {
            options: InferredProgressOptions,
          },
        },
        {
          fieldName: 'dueDate',
          fieldLabel: due_date_label,
          type: FieldType.Date,
          fieldId: FIELD_ID_DUE_DATE,
        },
        {
          fieldName: 'startDate',
          fieldLabel: start_date_label,
          type: FieldType.Date,
          fieldId: FIELD_ID_START_DATE,
        },
        {
          fieldLabel: last_status_update_label,
          fieldName: 'lastStatusUpdate',
          type: FieldType.Date,
          fieldId: FIELD_ID_LAST_STATUS_UPDATE,
        },
        {
          fieldLabel: progress_percent_label,
          fieldName: 'progressPercent',
          type: FieldType.Number,
          fieldId: FIELD_ID_PROGRESS_PERCENT,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: project_age_label,
          fieldName: 'projectAge',
          type: FieldType.Number,
          fieldId: FIELD_ID_PROJECT_AGE,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: planned_duration_label,
          fieldName: 'plannedDuration',
          type: FieldType.Number,
          fieldId: FIELD_ID_PLANNED_DURATION,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: actual_start_date_label,
          fieldName: 'startDateActual',
          type: FieldType.Date,
          fieldId: FIELD_ID_ACTUAL_START_DATE,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: actual_completed_date_label,
          fieldName: 'dueDateActual',
          type: FieldType.Date,
          fieldId: FIELD_ID_ACTUAL_COMPLETED_DATE,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: customersInvitedLabel,
          fieldName: 'customerMembersInvited',
          type: FieldType.Number,
          fieldId: FIELD_ID_CUSTOMERS_INVITED,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: customersAcceptedLabel,
          fieldName: 'customerMembersAccepted',
          type: FieldType.Number,
          fieldId: FIELD_ID_CUSTOMERS_ACCEPTED,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: projectCreatedOnLabel,
          fieldName: 'createdAt',
          type: FieldType.Date,
          fieldId: FIELD_ID_CREATED_ON,
        },
        ...(isTimeSheetEnabled
          ? [
              {
                fieldLabel: trackedHoursLabel,
                fieldName: ProjectTimeTrackingFieldsName.trackedHours,
                type: FieldType.Number,
                fieldId: FIELD_ID_TRACKED_HOURS,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: budgetHoursLabel,
                fieldName: ProjectTimeTrackingFieldsName.budgetedHours,
                type: FieldType.Number,
                fieldId: FIELD_ID_BUDGETED_HOURS,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: allocatedHoursLabel,
                fieldName: ProjectTimeTrackingFieldsName.allocatedHours,
                type: FieldType.Number,
                fieldId: FIELD_ID_ALLOCATED_HOURS,
                metaData: {
                  required: true,
                },
              },
            ]
          : []),
      ].map(
        (x) =>
          ({
            ...x,
            customField: false,
            private: false,
            sourceType: SourceType.Project,
          } as FilterableField),
      ),
    [
      arr_label,
      project_name_label,
      project_fee_label,
      inferred_progress_label,
      due_date_label,
      start_date_label,
      last_status_update_label,
      progress_percent_label,
      project_age_label,
      planned_duration_label,
      actual_start_date_label,
      actual_completed_date_label,
      customersInvitedLabel,
      customersAcceptedLabel,
      projectCreatedOnLabel,
      isTimeSheetEnabled,
    ],
  );
  const { templateField, isLoading: isTemplateFieldLoading } =
    useTemplateField(FIELD_ID_TEMPLATE);
  const { projectOwnerField, isLoading: isProjectOwnerFieldLoading } =
    useProjectOwnerField(FIELD_ID_PROJECT_OWNER);
  const { customerField, isLoading: isCustomerFieldLoading } =
    useCustomerField(FIELD_ID_CUSTOMER);
  const { teamMemberField, isLoading: isTeamMembersFieldLoading } =
    useTeamMembersField(FIELD_ID_TEAM_MEMBERS);
  const { currentPhasesField, isLoading: isCurrentPhasesFieldLoading } =
    useCurrentPhasesField(FIELD_ID_CURRENT_PHASES);
  const {
    partnerCompaniesField,
    isLoading: isPartnerCompaniesFieldLoading,
    isAnyPartnerCompanyAvailable,
  } = usePartnerCompanyField(FIELD_ID_PARNTER_COMPANIES);

  const fields = useMemo(
    () =>
      [
        ...projectFields,
        templateField,
        projectOwnerField,
        customerField,
        teamMemberField,
        currentPhasesField,
        isAnyPartnerCompanyAvailable ? partnerCompaniesField : undefined,
      ].filter((x) => Boolean(x)) as FilterableField[],
    [
      projectFields,
      templateField,
      projectOwnerField,
      customerField,
      teamMemberField,
      currentPhasesField,
      isAnyPartnerCompanyAvailable,
      partnerCompaniesField,
    ],
  );

  const isFieldsLoading = useMemo(
    () =>
      isProjectOwnerFieldLoading ||
      isCustomerFieldLoading ||
      isTeamMembersFieldLoading ||
      isCurrentPhasesFieldLoading ||
      isTemplateFieldLoading ||
      isPartnerCompaniesFieldLoading,
    [
      isProjectOwnerFieldLoading,
      isCustomerFieldLoading,
      isTeamMembersFieldLoading,
      isCurrentPhasesFieldLoading,
      isTemplateFieldLoading,
      isPartnerCompaniesFieldLoading,
    ],
  );
  const hasAccessToField = useProjectFieldsAccess();

  return useMemo(
    () => ({
      fields: fields.filter(hasAccessToField),
      loading: isFieldsLoading,
    }),
    [fields, hasAccessToField, isFieldsLoading],
  );
};

export const useProjectNativeFieldsForVariables = () => {
  const { fields, loading } = useProjectNativeFieldsAsFilterableFields();

  return useMemo(
    () => ({
      loading,
      fields: fields.filter(({ fieldId }) =>
        [
          FIELD_ID_PROJECT_NAME,
          FIELD_ID_START_DATE,
          FIELD_ID_DUE_DATE,
          FIELD_ID_ARR,
          FIELD_ID_PROJECT_FEE,
          FIELD_ID_BUDGETED_HOURS,
        ].includes(fieldId),
      ),
    }),
    [fields, loading],
  );
};
